import { ArrowLeft } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'link'
    | 'danger'
    | 'ghost'
    | 'disabled';
  fullWidth?: boolean;
  children: React.ReactNode;
  suffixIcon?: React.ReactNode;
  prefixIcon?: React.ReactNode;
}

export function Button({
  variant = 'primary',
  fullWidth = false,
  children,
  className = '',
  suffixIcon,
  prefixIcon,
  disabled,
  ...props
}: ButtonProps) {
  // ONE interaction standard for every tappable: hover state, deeper pressed
  // (active) state, focus-visible ring. Solid variants darken; text variants
  // (ghost/link) get the brandLight well - same treatment as the app-bar Back.
  const baseStyles =
    'rounded-sm font-normal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 flex items-center gap-2 justify-center';
  const variantStyles = {
    primary:
      'px-6 py-3 bg-brandOrange hover:bg-brandOrange/90 active:bg-brandOrange/80 text-white focus-visible:ring-brandOrange',
    secondary:
      'px-6 py-3 bg-primaryColor hover:bg-brand active:bg-primaryColor text-white focus-visible:ring-brand',
    danger:
      'px-6 py-3 bg-red-600 hover:bg-red-600/90 active:bg-red-600/80 text-white focus-visible:ring-brand',
    outline:
      'px-6 py-3 bg-white hover:bg-brandLight/30 active:bg-brandLight/50 text-primaryColor border border-primaryColor focus-visible:ring-primaryColor',
    ghost:
      'px-3 py-2 bg-transparent hover:bg-brandLight/30 active:bg-brandLight/50 focus-visible:ring-primaryColor',
    disabled: 'px-6 py-3 bg-inkDisabled text-black cursor-not-allowed',
    link: 'px-2 py-1 bg-transparent text-brand hover:bg-brandLight/30 active:bg-brandLight/50 focus-visible:ring-primaryColor',
  };
  const widthStyles = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`${baseStyles} ${!disabled ? variantStyles[variant] : variantStyles.disabled} ${widthStyles} ${className}`}
      {...props}
      disabled={disabled}
    >
      {prefixIcon && prefixIcon}
      {children}
      {suffixIcon && suffixIcon}
    </button>
  );
}

export function CompleteButton({ onClick }: { onClick?: () => void }) {
  return (
    <div className="mt-4 flex flex-col items-center justify-center space-y-4 px-4">
      <Button variant="primary" onClick={onClick}>
        <ArrowLeft size={24} />
        Mark as complete
      </Button>
    </div>
  );
}

export function ButtonSameWidthContainer({
  children,
}: {
  children: React.ReactElement<typeof Button>[];
}) {
  return (
    <div className="button-group-container mx-auto mt-4 flex w-full flex-col space-y-4 md:max-w-[275px]">
      {children}
    </div>
  );
}
